
import _ from "lodash";
import { getCategorizedVwTests, removeVwNodeById, getNutriprozIdToRemove, isUSCustomer, getGeneticsTestIds, filterGeneticsCategoryForNonUS } from "./helpers/tests";
import { getVaVwTestsAndDiscountPanels } from "@/utils/va-order-test/best-deal-extract/helpers.js";
import { getAllDescendants } from "@/utils/va-order-test/testAndPanelSelection/testSelection";
import { parseJson } from "@/utils";

const tests = {
  namespaced: true,
  state: {
    vaRootNodeList: [], // list of va tree roots
    vaBfsList: [], // list of node test for all va trees
    vwRootNodeList: [],
    vwBfsList: [],
    selectedTestIdList: [],
    vwCategories: [],
    regenereIdList: [], // regenere test array from API
    nutriprozIdList: [], // nutriproz test array from API
    isVaPanelHidden: false, // true for non-US clinics, or US clinics with hide_va and no patient va_eligibility
  },
  getters: {
    bfsTestList(state) {
      return state.vaBfsList.concat(state.vwBfsList);
    },
    vaTestList(state) {
      return state.vaBfsList.map(node => node.value);
    },
    vwTestList(state) {
      return state.vwBfsList.map(node => node.value);
    },
    testList(state, getters) {
      return getters.vaTestList.concat(getters.vwTestList);
    },
    nonOrderableTestIds(state, getters) {
      let nonOrderableTestNodeList = getters.bfsTestList.filter(t => t.value.toBeOffline);
      // hardcode micronutrient test id list
      let micronutrientTestIds = [27, 28, 394, 395, 396, 397, 398, 399, 400, 401, 402, 403, 404, 405, 406, 407, 29, 408, 409, 410, 30, 411, 412, 413, 414, 415, 416, 417, 418, 31, 419, 420, 32, 421, 422, 423, 424, 33, 425, 426, 427, 428, 429, 430, 431, 34, 115, 432, 433, 434, 116, 580];
      let nonMicronutrientTestIds = nonOrderableTestNodeList
        .filter(n => !micronutrientTestIds.includes(n.id))
        .map(n => n.id);
      let ctp = nonOrderableTestNodeList.find(node => node.id === 18);
      let ctpNodeList = ctp ? [ctp] : [];
      getAllDescendants(ctp, ctpNodeList);
      let ctpTestIds = ctpNodeList.map(n => n.id);
      // List that excludes both micronutrient and ctp test ids
      let excludedIds = [...new Set([...micronutrientTestIds, ...ctpTestIds])];
      let noMicronutrientAndNoCtpTestIds = nonOrderableTestNodeList
        .filter(n => !excludedIds.includes(n.id))
        .map(n => n.id);
      return {
        ctp: ctpTestIds,
        micronutrient: micronutrientTestIds,
        nonMicronutrient: nonMicronutrientTestIds,
        noMicronutrientAndNoCtp: noMicronutrientAndNoCtpTestIds
      };
    },
    selectedVaTestCount(state, getters) {
      return getters.vaTestList.filter(test => test.isSelect).length;
    },
    selectedVwTestCount(state, getters) {
      return getters.vwTestList.filter(test => test.isSelect && !test.toBeOffline).length;
    },
    selectedTestList(state, getters) {
      return getters.testList
        .filter(test => state.selectedTestIdList.includes(test.id));
    },
    serumCategorizedVwTests(state, getters, rootState) {
      let { vwCategories, vwBfsList } = state;
      let dbsMap = rootState.orderTest.dbsMap;
      let userInfo = rootState.orderTest.user.userInfo;
      return getCategorizedVwTests(vwCategories, vwBfsList, dbsMap, false, userInfo);
    },
    dbsCategorizedVwTests(state, getters, rootState) {
      let { vwCategories, vwBfsList } = state;
      let dbsMap = rootState.orderTest.dbsMap;
      let userInfo = rootState.orderTest.user.userInfo;
      return getCategorizedVwTests(vwCategories, vwBfsList, dbsMap, true, userInfo);
    },
    regenereNodeList(state) {
      // Filter vwRootNodeList using regenereIdList from store 
      // and order by display name in descending order to ensure
      // regenere starter display before regenere follow up
      return state.vwRootNodeList
        .filter(node => state.regenereIdList.includes(node.id))
        .sort((a, b) => {
          const nameA = (a.value.displayName || a.value.testName || '').toLowerCase();
          const nameB = (b.value.displayName || b.value.testName || '').toLowerCase();
          return nameB.localeCompare(nameA);
        });
    },
    nutriprozNodeList(state, getters, rootState) {
      // Filter vwRootNodeList using nutriprozIdList from store 
      // Remove NutriProZ(725) for NY customer, & remove NutriProZ NY(726) for non-NY user
      let userInfo = rootState.orderTest.user.userInfo;
      let nutriprozIdToRemove = getNutriprozIdToRemove(userInfo);
      let filteredNutriprozIds = state.nutriprozIdList.filter(id => id !== nutriprozIdToRemove);
      return state.vwRootNodeList.filter(node => filteredNutriprozIds.includes(node.id));
    },
  },
  mutations: {
    SET_VA_ROOT_NODE_LIST(state, rootNodeList) {
      state.vaRootNodeList = rootNodeList;
    },
    SET_VA_BFS_LIST(state, vaNodeList) {
      state.vaBfsList = vaNodeList;
    },
    SET_VW_ROOT_NODE_LIST(state, rootNodeList) {
      state.vwRootNodeList = rootNodeList;
    },
    SET_VW_BFS_LIST(state, vwNodeList) {
      state.vwBfsList = vwNodeList;
    },
    ADD_VA_TEST_NODE(state, vaTestNode) {
      state.vaBfsList.push(vaTestNode);
    },
    SET_VW_CATEGORIES(state, vwCategories) {
      state.vwCategories = vwCategories;
    },
    SET_REGENERE_ID_LIST(state, regenereIdList) {
      state.regenereIdList = regenereIdList || [];
    },
    SET_NUTRIPROZ_ID_LIST(state, nutriprozIdList) {
      state.nutriprozIdList = nutriprozIdList || [];
    },
    SET_VA_PANEL_HIDDEN(state, isHidden) {
      state.isVaPanelHidden = isHidden;
    },
  },
  actions: {
    async setAllTests({ commit, dispatch, rootState }, isAllTestsWithTubeInfo) {
      try {
        console.log('[setAllTests] setAllTests START', new Date().toISOString());
        let currency = parseJson(localStorage.getItem("currency"));
        let currencyName = currency.name ? currency.name.toLowerCase() : "usd";
        let { vaRootNodeList, vaNodeList, vwRootNodeList, vwNodeList, discountPanelList, exceptions, discountPanelCategory, vwTestCategory, dbsMappings, regenere, nutriproz }
          = await getVaVwTestsAndDiscountPanels(isAllTestsWithTubeInfo, currencyName);
        // VP-16225: Always refresh userInfo to ensure customer_country and beta_programs are current
        await dispatch("orderTest/user/setUserInfo", null, { root: true });
        // remove Regenere(skin care, id: 731) & nutriproZ (id: 725, 726) for rupa (id: 1007150) or "agent" role
        let { user_roles, old_clinic_id } = rootState.orderTest.user.userInfo;
        if (old_clinic_id === 1007150 || user_roles.includes("agent")) {
          let rootNodeNeedRemoved = vwRootNodeList.filter(test => [731, 725, 726].includes(test.id));
          rootNodeNeedRemoved.forEach(test => {
            let index = vwRootNodeList.indexOf(test);
            vwRootNodeList.splice(index, 1);
          });
          // remove same tests node in vwNodeList
          let nodeNeedRemoved = vwNodeList.filter(test => [731, 725, 726].includes(test.id));
          nodeNeedRemoved.forEach(test => {
            let index = vwNodeList.indexOf(test);
            vwNodeList.splice(index, 1);
          })
        }

        // remove Tickborne Antibodies (id: 832) if not in tickbone_customer beta
        let { beta_programs } = rootState.orderTest.user.userInfo;
        let isTickBorneAntibodiesBeta = beta_programs.includes("tickbone_customer") ? true : false;
        if (!isTickBorneAntibodiesBeta) {
          removeVwNodeById(vwRootNodeList, vwNodeList, 832);
        }

        // VP-15209: Remove Gut Zoomer - Stool Only (id: 836) if not in gz4_whitelist beta
        let isGz4WhitelistBeta = beta_programs && beta_programs.includes("gz4_whitelist");
        if (!isGz4WhitelistBeta) {
          removeVwNodeById(vwRootNodeList, vwNodeList, 836);
        }

        // VP-15638: Remove Hormone Zoomer - Urine Only (id: 835) if not in hz_urine_whitelist beta
        let isHzUrineWhitelistBeta = beta_programs && beta_programs.includes("hz_urine_whitelist");
        if (!isHzUrineWhitelistBeta) {
          removeVwNodeById(vwRootNodeList, vwNodeList, 835);
        }

        // VP-15644: Remove Nutrient Zoomer Microtube (id: 850) if not in nzm_whitelist beta
        let isNzmWhitelistBeta = beta_programs && beta_programs.includes("nzm_whitelist");
        if (!isNzmWhitelistBeta) {
          removeVwNodeById(vwRootNodeList, vwNodeList, 850);
        }

        // VP-14690: Remove Genetics category tests for non-US users
        // VP-15061: Do not remove if user has genetics_whitelist beta.  
        let userInfo = rootState.orderTest.user.userInfo;
        let hasGeneticsWhitelist = userInfo?.beta_programs && Array.isArray(userInfo.beta_programs) && userInfo.beta_programs.includes("genetics_whitelist");
        if (!isUSCustomer(userInfo) && !hasGeneticsWhitelist) {
          let geneticsTestIds = getGeneticsTestIds(vwTestCategory);
          geneticsTestIds.forEach(testId => {
            removeVwNodeById(vwRootNodeList, vwNodeList, testId);
          });
          vwTestCategory = filterGeneticsCategoryForNonUS(vwTestCategory, userInfo);
        }

        commit("SET_VA_PANEL_HIDDEN", false); // reset; re-evaluated after patient is selected
        commit("SET_VA_ROOT_NODE_LIST", vaRootNodeList);
        commit("SET_VA_BFS_LIST", vaNodeList);
        commit("SET_VW_ROOT_NODE_LIST", vwRootNodeList);
        commit("SET_VW_BFS_LIST", vwNodeList);
        // discount panels
        dispatch("orderTest/panels/setDiscountPanelList", discountPanelList, { root: true });
        // set exceptions
        dispatch("orderTest/exceptions/setExceptionMap", exceptions, { root: true });
        // set discount panel filter tags
        dispatch("orderTest/panels/setDiscountPanelFilterTagList", discountPanelCategory, { root: true });
        // set vw test categories
        dispatch("setVwCategories", vwTestCategory);
        // set dbsMappings
        await dispatch("orderTest/setDbsMap", dbsMappings, { root: true });
        // set regenere and nutriproz lists (with undefined handling: set to empty array if backend not given)
        commit("SET_REGENERE_ID_LIST", regenere || []);
        commit("SET_NUTRIPROZ_ID_LIST", nutriproz || []);
      } catch (err) {
        console.log("setAllTests error", err);
      }
    },

    addVaTestNode({ commit }, vaTestNode) {
      commit("ADD_VA_TEST_NODE", vaTestNode);
    },

    setVaPanelVisibility({ commit, state }, { isHidden }) {
      state.vaBfsList.forEach(node => {
        if (isHidden) {
          node.value.originalToBeOffline = node.value.toBeOffline;
          node.value.toBeOffline = true;
        } else if ("originalToBeOffline" in node.value) {
          node.value.toBeOffline = node.value.originalToBeOffline;
          delete node.value.originalToBeOffline;
        }
      });
      commit("SET_VA_PANEL_HIDDEN", isHidden);
    },

    async setVwCategories({ commit }, categories) {
      let vwCategories = [];
      categories.forEach((c, index) => {
        vwCategories.push({
          index: index,
          categoryId: index,
          categoryName: c.name,
          categoryNames: { ...c.names, english: c.name },
          contents: c.testIds
        });
      });

      // sort the array in ascending index order
      vwCategories.sort((a, b) => a.index - b.index);

      commit("SET_VW_CATEGORIES", vwCategories);
    },
  }
};

export default tests;